import type { ActKc2UerCostPlotData, WorkOrderSeasonData } from 'gql/graphql';
import { Area } from 'uikit';
import { capitalize, formatNumberToRu } from 'utils';
import { getMonthName } from 'utils/formatDateToLocale';

import React, { useMemo } from 'react';

import { AnalyticsTooltip } from './AnalyticsTooltip';

import Style from './AnalyticsBySeasonsChart.module.css';

interface IAnalyticsBySeasons {
    items?: ActKc2UerCostPlotData[] | null;
    workOrderSeasons: WorkOrderSeasonData[];
}

export const areaStyle = ({ type }: any) => ({
    fill:
        type === 'Принято в Акте КС-2'
            ? `l(179.75) 0:rgba(54, 144, 208, 0.15) -9.41% 1:rgba(54, 144, 208, 0.075) 102.65%);
`
            : `l(149.53) 0:rgba(18, 180, 126, 0.2) 32.79% 1:rgba(18, 180, 126, 0) 96.01%);
`,
});

const xAxis = {
    grid: {
        line: {
            style: (_: any, index: number) => ({
                stroke: 'rgba(0, 65, 102, 0.2)',
                lineWidth: Number.isInteger(index / 12) ? 3 : 1,
            }),
        },
    },
    label: {
        formatter: (v: string) => (v.startsWith('Июнь') ? v.substring(4) : ''),
        style: {
            fontSize: 16,
        },
    },
};

export const yAxis = {
    grid: { line: { style: { stroke: 'rgba(0, 65, 102, 0.2)' } } },
    label: {
        formatter: (v: any) => formatNumberToRu(Number(v), 0),
        style: {
            fontSize: 16,
        },
    },
};

export const tooltip = {
    formatter: (data: any) => ({
        name: data.type,
        value: `${formatNumberToRu(Number(data.value), 0)}`,
    }),
    customContent: (title: string, data: any[]) =>
        title === 'start' ? '' : <AnalyticsTooltip title={title} data={data} />,
};

export const AnalyticsBySeasonsChart: React.FC<IAnalyticsBySeasons> = (props) => {
    const { items, workOrderSeasons } = props;

    const areaData = useMemo(
        () =>
            workOrderSeasons?.reduce((acc: any[], season, seasonIndex) => {
                for (let i = 0; i < 12; i++) {
                    const tickDate = new Date(season.year, i, 1);
                    const itemToSet = items?.find((iAct) => {
                        const itemDate = new Date(iAct.date);

                        return (
                            itemDate.getMonth() === tickDate.getMonth() &&
                            itemDate.getFullYear() === tickDate.getFullYear()
                        );
                    });

                    if (!seasonIndex && !i) {
                        acc.push({
                            date: 'start',
                            type: 'Принято в Акте КС-2',
                            value: 0,
                        });
                        acc.push({
                            date: 'start',
                            type: 'Согласовано СКзC',
                            value: 0,
                        });
                    }

                    const date = `${capitalize(getMonthName(tickDate.getMonth() + 1))} ${tickDate.getFullYear()}`;

                    acc.push({
                        date,
                        type: 'Принято в Акте КС-2',
                        value: itemToSet?.rimActKC2WorkCost || 0,
                    });

                    acc.push({
                        date,
                        type: 'Согласовано СКзC',
                        value: itemToSet?.skzsActKC2WorkCost,
                    });
                }

                return acc;
            }, []) || [],
        [items, workOrderSeasons],
    );

    return (
        <Area
            className={Style.analyticsBySeasonsChart}
            xField="date"
            yField="value"
            seriesField="type"
            data={areaData}
            xAxis={xAxis}
            yAxis={yAxis}
            legend={false}
            areaStyle={areaStyle}
            isStack={false}
            tooltip={tooltip}
        />
    );
};

AnalyticsBySeasonsChart.displayName = 'AnalyticsBySeasons';

export const MemoizedAnalyticsBySeasonsChart = React.memo(AnalyticsBySeasonsChart);
